import React, { useState, useCallback, useEffect } from 'react';
import { useAuth, SSODiscoveryResult } from '../../lib/auth/AuthProvider';

export function LoginPage() {
  const { signInWithGoogle, signInWithEmail, signInWithSSO, discoverSSO } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // SSO state
  const [ssoResult, setSSOResult] = useState<SSODiscoveryResult | null>(null);
  const [isCheckingSSO, setIsCheckingSSO] = useState(false);
  const [emailDebounceTimer, setEmailDebounceTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  // Check for SSO when email changes (debounced)
  const checkSSO = useCallback(async (emailToCheck: string) => {
    if (!emailToCheck || !emailToCheck.includes('@')) {
      setSSOResult(null);
      return;
    }

    setIsCheckingSSO(true);
    try {
      const result = await discoverSSO(emailToCheck);
      setSSOResult(result);
    } catch (err) {
      console.error('SSO check failed:', err);
      setSSOResult(null);
    } finally {
      setIsCheckingSSO(false);
    }
  }, [discoverSSO]);

  // Debounce email changes for SSO discovery
  useEffect(() => {
    if (emailDebounceTimer) {
      clearTimeout(emailDebounceTimer);
    }

    if (email && email.includes('@')) {
      const timer = setTimeout(() => {
        checkSSO(email);
      }, 500);
      setEmailDebounceTimer(timer);
    } else {
      setSSOResult(null);
    }

    return () => {
      if (emailDebounceTimer) {
        clearTimeout(emailDebounceTimer);
      }
    };
  }, [email]);

  const handleGoogleSignIn = async () => {
    setError(null);
    setIsLoading(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      setError('Failed to sign in with Google');
      setIsLoading(false);
    }
  };

  const handleSSOSignIn = () => {
    if (!ssoResult?.organizationSlug) return;

    setError(null);
    setIsLoading(true);
    signInWithSSO(ssoResult.organizationSlug);
    // Note: This redirects, so we don't need to set isLoading back to false
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();

    // If SSO is enforced, don't allow email/password login
    if (ssoResult?.ssoEnforced) {
      setError('Your organization requires SSO login. Please use the SSO button.');
      return;
    }

    setError(null);
    setIsLoading(true);
    try {
      await signInWithEmail(email, password);
    } catch (err) {
      setError('Invalid email or password');
      setIsLoading(false);
    }
  };

  // Determine if SSO is available/required
  const showSSOButton = ssoResult?.ssoEnabled;
  const ssoEnforced = ssoResult?.ssoEnforced;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <div className="w-full max-w-md p-8">
        <div className="bg-card rounded-lg shadow-xl p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Snailman
            </h1>
            <p className="text-muted-foreground">
              A collaborative API testing tool
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* SSO Button (when available) */}
          {showSSOButton && (
            <>
              <button
                onClick={handleSSOSignIn}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-4"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                {isLoading ? 'Redirecting...' : `Sign in with ${ssoResult?.organizationName || 'SSO'}`}
              </button>

              {ssoEnforced && (
                <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-md">
                  <p className="text-sm text-blue-400">
                    Your organization requires SSO login.
                  </p>
                </div>
              )}

              {!ssoEnforced && (
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border"></div>
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">
                      Or continue with
                    </span>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Google Sign In (hidden if SSO is enforced) */}
          {!ssoEnforced && (
            <>
              <button
                onClick={handleGoogleSignIn}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white text-gray-700 rounded-md border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {isLoading ? 'Signing in...' : 'Continue with Google'}
              </button>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">
                    Or continue with email
                  </span>
                </div>
              </div>

              {/* Email Sign In Form */}
              <form onSubmit={handleEmailSignIn} className="space-y-4">
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-foreground mb-1"
                  >
                    Email
                  </label>
                  <div className="relative">
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                      required
                    />
                    {isCheckingSSO && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <svg className="animate-spin h-4 w-4 text-muted-foreground" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      </div>
                    )}
                    {ssoResult?.ssoEnabled && !isCheckingSSO && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <svg className="h-4 w-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  {ssoResult?.ssoEnabled && !ssoEnforced && (
                    <p className="mt-1 text-xs text-green-600">
                      SSO available for {ssoResult.organizationName}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-foreground mb-1"
                  >
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="********"
                    className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                    required
                    disabled={ssoEnforced}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading || ssoEnforced}
                  className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Signing in...' : 'Sign in'}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-sm text-white/60 mt-4">
          Built with care for API developers
        </p>
      </div>
    </div>
  );
}
